import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import StatsCard from '../components/StatsCard';
import RecentActivity from '../components/RecentActivity';
import AlertsPanel from '../components/AlertsPanel';
import MalawiDistrictsMap from '../components/MalawiMapOnly';
import IncidentReportForm from '../components/IncidentReportForm';
import ObserverDayWizard from '../components/ObserverDayWizard';
import MyIncidentsList from '../components/MyIncidentsList';
import MyReportsList from '../components/MyReportsList';
import {
  DocumentTextIcon,
  ExclamationTriangleIcon,
  UsersIcon,
  ListBulletIcon,
  ClipboardDocumentCheckIcon,
  ChatBubbleLeftRightIcon,
  PlusIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';

// Helper function to capitalize first letter and make rest lowercase
const capitalizeFirstLetter = (name: string | undefined) => {
  if (!name) return '';
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

// Animated Counter Component - Memoized to prevent unnecessary re-renders
const AnimatedCounter = React.memo<{ totalIncidents: number; textColor?: string }>(({ totalIncidents, textColor = "text-red-600" }) => {
  const [count, setCount] = React.useState(0);

  React.useEffect(() => {
    if (totalIncidents === 0) {
      setCount(0);
      return;
    }

    const duration = 2000; // 2 seconds
    const increment = totalIncidents / (duration / 16); // 60fps
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= totalIncidents) {
        setCount(totalIncidents);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [totalIncidents]);

  return (
    <span className={`text-sm font-semibold tabular-nums ${textColor}`}>
      {count.toLocaleString()}
    </span>
  );
});

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Only admins load admin-wide datasets; coordinators now have a limited UI
  const enabledForAdmins = !!user && user.role === 'admin';
  const [showIncidentForm, setShowIncidentForm] = useState(false);
  const [showEdayForm, setShowEdayForm] = useState(false);
  const [showMyIncidents, setShowMyIncidents] = useState(false);
  const [showMyReports, setShowMyReports] = useState(false);


  // NEW: location filters (IDs are cleanest)
  const [filters, setFilters] = useState<{
    district_id?: number;
    constituency_id?: number;
    ward_id?: number;
  }>({});

  // useQuery now depends on filters and user.id
  const { data: overview, isLoading: overviewLoading } = useQuery(
    ['dashboard-overview', user?.id, filters],
    () => api.dashboard.getOverview(filters),
    { refetchInterval: 120000 } // 2 minutes
  );

  // Pull the lookups out of the response safely
  const lookups = overview?.data?.data?.lookups;
  // Debug logging removed - functionality working correctly



  // useEffect(() => {
  // if (overview?.data?.data) {
  // console.log('🔎 overview payload:', overview.data.data);
  // console.log('📦 lookups from /overview:', overview.data.data.lookups);
  // }
  // }, [overview]);

  // Show success message if redirected with state - Optimized to prevent re-render loops
  React.useEffect(() => {
    if (location.state && location.state.message) {
      const { message, type } = location.state;
      if (type === 'success') {
        toast.success(message);
      } else if (type === 'error') {
        toast.error(message);
      } else {
        toast(message);
      }
      // Clear the state to prevent showing the message again
      const currentPath = location.pathname;
      navigate(currentPath, { replace: true, state: null });
    }
  }, [location.state, navigate, location.pathname]); // Include dependencies as required

  // const { data: overview, isLoading: overviewLoading } = useQuery(
  // ['dashboard-overview', user?.id],
  // () => api.dashboard.getOverview(),
  // { refetchInterval: 30000, enabled: enabledForAdmins }
  // );

  // console.log(' overview:', overview);

  const { data: pollingStations, isLoading: pollingStationsLoading, error: pollingStationsError } = useQuery(
    ['dashboard-polling-stations', user?.id],
    () => api.dashboard.getPollingStations(),
    {
      refetchInterval: 360000, // 6 minutes
      enabled: enabledForAdmins,
      retry: 2,
      retryDelay: 5000, // 5 seconds between retries
      staleTime: 300000, // 5 minutes stale time
      cacheTime: 600000, // 10 minutes cache time
      refetchOnWindowFocus: false,
      onError: (error: any) => {
        console.error('🚨 Polling Stations API Error:', error);
        if (error?.code === 'ECONNABORTED') {
          toast.error('Polling stations data request timed out. This endpoint takes longer to load.');
        } else {
          toast.error('Failed to load polling stations data');
        }
      }
    }
  );



  const { data: compliance, isLoading: complianceLoading } = useQuery(
    ['dashboard-compliance', user?.id],
    () => api.dashboard.getCompliance(),
    { refetchInterval: 120000, enabled: enabledForAdmins } // 2 minutes
  );


  const { data: performance, isLoading: performanceLoading } = useQuery(
    ['dashboard-performance', user?.id],
    () => api.dashboard.getPerformance(),
    { refetchInterval: 120000, enabled: enabledForAdmins } // 2 minutes
  );

  const { data: recentActivity, isLoading: activityLoading } = useQuery(
    ['recent-activity', user?.id],
    () => api.dashboard.getRecentActivity({ limit: 10 }),
    { refetchInterval: 120000, enabled: enabledForAdmins } // 2 minutes
  );

  const { data: alerts, isLoading: alertsLoading } = useQuery(
    ['dashboard-alerts', user?.id],
    () => api.dashboard.getAlerts(),
    { refetchInterval: 120000, enabled: enabledForAdmins } // 2 minutes
  );

  const { data: incidentsMap, isLoading: incidentsMapLoading, error: incidentsMapError } = useQuery(
    ['dashboard-incidents-map', user?.id],
    async () => api.dashboard.getIncidentsMap(),
    {
      refetchInterval: 120000, // 2 minutes
      retry: 1,
      retryDelay: 2000,
      enabled: enabledForAdmins,
      staleTime: 60000, // 1 minute stale time
      cacheTime: 600000, // 10 minutes cache time
      refetchOnWindowFocus: false,
      onError: (error: any) => {
        console.error('🚨 Incidents Map API Error:', error);
        if (error?.code === 'ECONNABORTED') {
          toast.error('Map data request timed out. Retrying...');
        } else {
          toast.error('Failed to load incidents map data');
        }
      }
    }
  );

  // Observer progress query (only for observers)
  const { data: observerProgress, isLoading: observerProgressLoading, error: observerProgressError } = useQuery(
    ['observer-progress', user?.id],
    () => api.dashboard.getObserverProgress(),
    {
      refetchInterval: 120000, // 2 minutes
      enabled: user?.role === 'observer',
      onError: (error) => {
        console.error('🚨 Observer Progress API Error:', error);
      },
      onSuccess: (data) => {
        // Observer progress API working correctly
      }
    }
  );

  // Observer assigned centers (by ward) to compute answered vs not-answered
  const { data: observerLookups, isLoading: observerLookupsLoading } = useQuery(
    ['observer-assigned-centers', user?.id, user?.district, user?.constituency, user?.ward],
    () => api.dashboard.getOverview({
      district: user?.district || '',
      constituency: user?.constituency || '',
      ward: user?.ward || '',
      include_all_polling: '1',
    }),
    {
      enabled: user?.role === 'observer' && !!user?.ward && !!user?.constituency && !!user?.district,
      refetchInterval: 120000, // 2 minutes
    }
  );

  // Build assigned center list and answered/missing per timeline for observer
  const assignedCenters: string[] = React.useMemo(() => {
    const pcs = observerLookups?.data?.data?.lookups?.pollingcenters || [];
    return pcs.map((pc: any) => pc.name).filter(Boolean);
  }, [observerLookups]);

  const answeredByTimeline = React.useMemo(() => {
    const tp = observerProgress?.data?.data?.observer_progress?.timeline_progress;
    return {
      morning: new Set<string>(tp?.morning?.centers || []),
      afternoon: new Set<string>(tp?.afternoon?.centers || []),
      vote_count: new Set<string>(tp?.vote_count?.centers || []),
    };
  }, [observerProgress]);

  const missingByTimeline = React.useMemo(() => {
    const computeMissing = (answered: Set<string>) =>
      assignedCenters.filter((c) => !answered.has(c));
    return {
      morning: computeMissing(answeredByTimeline.morning),
      afternoon: computeMissing(answeredByTimeline.afternoon),
      vote_count: computeMissing(answeredByTimeline.vote_count),
    };
  }, [assignedCenters, answeredByTimeline]);


  // Debug logging removed - incidents map working correctly

  // Handle incident form submission
  const handleIncidentSubmit = (data: any) => {
    // Form submission working correctly
    setShowIncidentForm(false);
    toast.success('Incident report submitted successfully!');
  };

  // Handle eday form submission  
  const handleEdaySubmit = () => {
    // E-day form submission working correctly
    setShowEdayForm(false);
    toast.success('E-Day report submitted successfully!');
  };

  // Show incident form modal
  if (showIncidentForm) {
    return (
      <IncidentReportForm
        onClose={() => setShowIncidentForm(false)}
        onSubmit={handleIncidentSubmit}
      />
    );
  }

  // Show eday wizard
  if (showEdayForm) {
    return <ObserverDayWizard key={user?.id || 'anon'} onClose={() => setShowEdayForm(false)} />;
  }

  // Show my incidents list
  if (showMyIncidents) {
    return <MyIncidentsList onClose={() => setShowMyIncidents(false)} />;
  }

  // Show my reports list
  if (showMyReports) {
    return <MyReportsList onClose={() => setShowMyReports(false)} />;
  }

  // Coordinator Dashboard View (limited to incident reports and E-Day checklist)
  if (user?.role === 'coordinator') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="container px-4 py-8 mx-auto sm:px-6 lg:px-8">
          <div className="relative overflow-hidden bg-white shadow-xl rounded-2xl">
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-600 opacity-10"></div>
            <div className="relative px-8 py-12 text-center">
              <div className="inline-flex items-center px-4 py-2 mb-4 text-xs font-medium text-indigo-700 bg-indigo-100 rounded-full">
                <div className="w-2 h-2 mr-2 bg-indigo-600 rounded-full animate-pulse"></div>
                Coordinator Portal
              </div>
              <h1 className="mb-4 text-4xl font-bold text-transparent xl:text-5xl bg-gradient-to-r from-gray-900 via-indigo-900 to-blue-900 bg-clip-text">
                Welcome Back, {capitalizeFirstLetter(user?.name)}
              </h1>
              <p className="max-w-2xl mx-auto mb-8 text-lg text-gray-600 xl:text-xl">
                Quick access to incident reporting and the E‑Day checklist.
              </p>

              {/* Action Cards Grid */}
              <div className="grid max-w-4xl grid-cols-1 gap-6 mx-auto sm:grid-cols-2">
                {/* Report Incident */}
                <button
                  onClick={() => navigate('/admin/incidents')}
                  className="relative p-8 overflow-hidden transition-all duration-300 transform border border-red-200 group bg-gradient-to-br from-red-50 via-pink-50 to-red-100 rounded-2xl hover:from-red-100 hover:via-pink-100 hover:to-red-200 hover:border-red-300 hover:shadow-xl hover:scale-105"
                >
                  <div className="absolute top-0 right-0 w-24 h-24 transform translate-x-8 -translate-y-8 bg-red-500 rounded-full opacity-10"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 transition-all duration-300 bg-red-100 rounded-xl group-hover:bg-red-200 group-hover:scale-110">
                      <ExclamationTriangleIcon className="w-8 h-8 text-red-600" />
                    </div>
                    <h3 className="mb-3 text-xl font-bold text-gray-900 xl:text-2xl">View all report</h3>
                    <p className="text-sm leading-relaxed text-gray-600 xl:text-base">
                      Log incidents and issues occurring at polling stations
                    </p>
                    <div className="flex items-center justify-center mt-4">
                      <div className="w-6 h-0.5 bg-red-300 rounded-full group-hover:w-12 transition-all duration-300"></div>
                    </div>
                  </div>
                </button>

                {/* E‑Day Checklist */}
                <button
                  onClick={() => navigate('/admin/reports')}
                  className="relative p-8 overflow-hidden transition-all duration-300 transform border border-blue-200 group bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 rounded-2xl hover:from-blue-100 hover:via-indigo-100 hover:to-blue-200 hover:border-blue-300 hover:shadow-xl hover:scale-105"
                >
                  <div className="absolute top-0 right-0 w-24 h-24 transform translate-x-8 -translate-y-8 bg-blue-500 rounded-full opacity-10"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 transition-all duration-300 bg-blue-100 rounded-xl group-hover:bg-blue-200 group-hover:scale-110">
                      <ClipboardDocumentListIcon className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="mb-3 text-xl font-bold text-gray-900 xl:text-2xl">E‑Day Checklist</h3>
                    <p className="text-sm leading-relaxed text-gray-600 xl:text-base">
                      Complete the election day observation checklist
                    </p>
                    <div className="flex items-center justify-center mt-4">
                      <div className="w-6 h-0.5 bg-blue-300 rounded-full group-hover:w-12 transition-all duration-300"></div>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Observer Dashboard View
  if (user?.role === 'observer') {
    // Observer dashboard - debugging removed, functionality working correctly

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="container px-4 py-8 mx-auto sm:px-6 lg:px-8">
          {/* Hero Section */}
          <div className="relative overflow-hidden bg-white shadow-xl rounded-2xl">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 opacity-10"></div>
            <div className="relative px-8 py-12 text-center">
              <div className="inline-flex items-center px-4 py-2 mb-4 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
                <div className="w-2 h-2 mr-2 bg-blue-600 rounded-full animate-pulse"></div>
                Observer Portal
              </div>
              <h1 className="mb-4 text-4xl font-bold text-transparent xl:text-5xl bg-gradient-to-r from-gray-900 via-blue-900 to-indigo-900 bg-clip-text">
                Welcome Back, {capitalizeFirstLetter(user?.name)}
              </h1>
              <p className="max-w-2xl mx-auto mb-8 text-lg text-gray-600 xl:text-xl">
                Your Election Day observation dashboard. Report incidents, submit E-Day reports, and track your contributions to transparent elections.
              </p>

              {/* Enhanced Action Cards Grid */}
              <div className="grid grid-cols-1 gap-6 mx-auto max-w-7xl sm:grid-cols-2 lg:grid-cols-4">
                {/* Submit Incident Report Button - Enhanced */}
                <button
                  onClick={() => setShowIncidentForm(true)}
                  className="relative p-8 overflow-hidden transition-all duration-300 transform border border-red-200 group bg-gradient-to-br from-red-50 via-pink-50 to-red-100 rounded-2xl hover:from-red-100 hover:via-pink-100 hover:to-red-200 hover:border-red-300 hover:shadow-xl hover:scale-105"
                >
                  <div className="absolute top-0 right-0 w-24 h-24 transform translate-x-8 -translate-y-8 bg-red-500 rounded-full opacity-10"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 transition-all duration-300 bg-red-100 rounded-xl group-hover:bg-red-200 group-hover:scale-110">
                      <ExclamationTriangleIcon className="w-8 h-8 text-red-600" />
                    </div>
                    <h3 className="mb-3 text-xl font-bold text-gray-900 xl:text-2xl">Submit Incident Report</h3>
                    <p className="text-sm leading-relaxed text-gray-600 xl:text-base">
                      Quickly report any irregularities or issues observed during the election process
                    </p>
                    <div className="flex items-center justify-center mt-4">
                      <div className="w-6 h-0.5 bg-red-300 rounded-full group-hover:w-12 transition-all duration-300"></div>
                    </div>
                  </div>
                </button>

                {/* Submit E-Day Report Button - Enhanced */}
                <button
                  onClick={() => setShowEdayForm(true)}
                  className="relative p-8 overflow-hidden transition-all duration-300 transform border border-blue-200 group bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 rounded-2xl hover:from-blue-100 hover:via-indigo-100 hover:to-blue-200 hover:border-blue-300 hover:shadow-xl hover:scale-105"
                >
                  <div className="absolute top-0 right-0 w-24 h-24 transform translate-x-8 -translate-y-8 bg-blue-500 rounded-full opacity-10"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 transition-all duration-300 bg-blue-100 rounded-xl group-hover:bg-blue-200 group-hover:scale-110">
                      <ClipboardDocumentListIcon className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="mb-3 text-xl font-bold text-gray-900 xl:text-2xl">Submit E-Day Report</h3>
                    <p className="text-sm leading-relaxed text-gray-600 xl:text-base">
                      Complete your comprehensive election day observation checklist and assessments
                    </p>
                    <div className="flex items-center justify-center mt-4">
                      <div className="w-6 h-0.5 bg-blue-300 rounded-full group-hover:w-12 transition-all duration-300"></div>
                    </div>
                  </div>
                </button>

                {/* My Submitted Incidents Button - Enhanced */}
                <button
                  onClick={() => setShowMyIncidents(true)}
                  className="relative p-8 overflow-hidden transition-all duration-300 transform border border-orange-200 group bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 rounded-2xl hover:from-orange-100 hover:via-amber-100 hover:to-orange-200 hover:border-orange-300 hover:shadow-xl hover:scale-105"
                >
                  <div className="absolute top-0 right-0 w-24 h-24 transform translate-x-8 -translate-y-8 bg-orange-500 rounded-full opacity-10"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 transition-all duration-300 bg-orange-100 rounded-xl group-hover:bg-orange-200 group-hover:scale-110">
                      <ListBulletIcon className="w-8 h-8 text-orange-600" />
                    </div>
                    <h3 className="mb-3 text-xl font-bold text-gray-900 xl:text-2xl">My Incidents</h3>
                    <p className="text-sm leading-relaxed text-gray-600 xl:text-base">
                      Review, track status, and manage all your submitted incident reports
                    </p>
                    <div className="flex items-center justify-center mt-4">
                      <div className="w-6 h-0.5 bg-orange-300 rounded-full group-hover:w-12 transition-all duration-300"></div>
                    </div>
                  </div>
                </button>

                {/* My E-Day Submissions Button - Enhanced */}
                <button
                  onClick={() => setShowMyReports(true)}
                  className="relative p-8 overflow-hidden transition-all duration-300 transform border border-green-200 group bg-gradient-to-br from-green-50 via-emerald-50 to-green-100 rounded-2xl hover:from-green-100 hover:via-emerald-100 hover:to-green-200 hover:border-green-300 hover:shadow-xl hover:scale-105"
                >
                  <div className="absolute top-0 right-0 w-24 h-24 transform translate-x-8 -translate-y-8 bg-green-500 rounded-full opacity-10"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 transition-all duration-300 bg-green-100 rounded-xl group-hover:bg-green-200 group-hover:scale-110">
                      <ClipboardDocumentCheckIcon className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="mb-3 text-xl font-bold text-gray-900 xl:text-2xl">My Reports</h3>
                    <p className="text-sm leading-relaxed text-gray-600 xl:text-base">
                      Access your submitted E-Day reports and monitor their processing status
                    </p>
                    <div className="flex items-center justify-center mt-4">
                      <div className="w-6 h-0.5 bg-green-300 rounded-full group-hover:w-12 transition-all duration-300"></div>
                    </div>
                  </div>
                </button>
              </div>

              {/* Observer Progress Cards */}
              {observerProgressLoading ? (
                <div className="flex items-center justify-center max-w-4xl mx-auto mt-12">
                  <div className="w-8 h-8 border-b-2 border-blue-500 rounded-full animate-spin"></div>
                  <span className="ml-3 text-gray-600">Loading your progress...</span>
                </div>
              ) : observerProgress?.data?.data?.observer_progress ? (
                <div className="max-w-6xl mx-auto mt-12">
                  {/* User Assignment Info */}
                  <div className="mb-6 text-center">
                    <h2 className="mb-1 text-sm font-bold text-gray-900 ">Your Assignment Progress</h2>
                    {user?.district && user?.constituency ? (
                      <>
                        <p className="text-gray-600 xl:text-lg">
                          {user.district} › {user.constituency}
                          {user.ward && user.ward !== 'All wards' && ` › ${user.ward}`}
                        </p>
                        <p className="text-sm text-gray-500 xl:text-base">
                          Assigned to {assignedCenters.length} polling centers
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-600 xl:text-lg">Verifying assignment details...</p>
                    )}
                  </div>

                  {/* Timeline Progress Cards */}
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                    {/* Morning Progress */}
                    <div className="relative p-6 overflow-hidden transition-all duration-300 border border-yellow-200 group bg-gradient-to-br from-yellow-50 via-amber-50 to-yellow-100 rounded-lg hover:shadow-lg hover:scale-105">
                      <div className="absolute top-0 right-0 w-20 h-20 transform translate-x-6 -translate-y-6 bg-yellow-500 rounded-full opacity-10"></div>
                      <div className="relative z-10">
                        <div className="flex items-center justify-between mb-4">
                          <div className="p-3 bg-yellow-100 rounded-xl">
                            <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-bold text-yellow-700 ">
                              {observerProgress.data.data.observer_progress.timeline_progress.morning.percentage}%
                            </div>
                            <div className="text-xs text-yellow-600 xl:text-sm">Complete</div>
                          </div>
                        </div>
                        <div className="mb-1 text-lg font-semibold text-yellow-800 xl:text-xl">Morning Reports</div>
                        <div className="mb-3 text-sm text-yellow-700 xl:text-base">
                          {observerProgress.data.data.observer_progress.timeline_progress.morning.reported} of {observerProgress.data.data.observer_progress.timeline_progress.morning.assigned} centers
                        </div>
                        {/* Progress bar */}
                        <div className="w-full h-2 mb-1 bg-yellow-200 rounded-full">
                          <div
                            className="h-2 transition-all duration-300 bg-yellow-500 rounded-full"
                            style={{ width: `${observerProgress.data.data.observer_progress.timeline_progress.morning.percentage}%` }}
                          ></div>
                        </div>
                        <div className="text-xs text-yellow-600 xl:text-sm">
                          {observerProgress.data.data.observer_progress.timeline_progress.morning.centers.length > 0 &&
                            `Last: ${observerProgress.data.data.observer_progress.timeline_progress.morning.centers.slice(-1)[0]}`
                          }
                        </div>
                        {/* Answered vs Not Answered list for Morning */}
                        {!(observerLookupsLoading || observerProgressLoading) && assignedCenters.length > 0 && (
                          <div className="mt-4 space-y-3">
                            <div>
                              <div className="mb-1 text-xs font-semibold text-yellow-800">Answered ({answeredByTimeline.morning.size})</div>
                              <div className="flex flex-wrap gap-1">
                                {Array.from(answeredByTimeline.morning).map((c) => (
                                  <span key={`am-ans-${c}`} className="px-2 py-0.5 text-xs text-yellow-900 bg-yellow-100 rounded-full border border-yellow-200">{c}</span>
                                ))}
                                {answeredByTimeline.morning.size === 0 && (
                                  <span className="text-xs text-yellow-700">No centers answered yet</span>
                                )}
                              </div>
                            </div>
                            <div>
                              <div className="mb-1 text-xs font-semibold text-red-800">Not Answered ({missingByTimeline.morning.length})</div>
                              <div className="flex flex-wrap gap-1">
                                {missingByTimeline.morning.map((c) => (
                                  <span key={`am-miss-${c}`} className="px-2 py-0.5 text-xs text-red-800 bg-red-100 rounded-full border border-red-200">{c}</span>
                                ))}
                                {missingByTimeline.morning.length === 0 && (
                                  <span className="text-xs text-green-700">All assigned centers answered</span>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Afternoon Progress */}
                    <div className="relative p-6 overflow-hidden transition-all duration-300 border border-orange-200 group bg-gradient-to-br from-orange-50 via-red-50 to-orange-100 rounded-lg hover:shadow-lg hover:scale-105">
                      <div className="absolute top-0 right-0 w-20 h-20 transform translate-x-6 -translate-y-6 bg-orange-500 rounded-full opacity-10"></div>
                      <div className="relative z-10">
                        <div className="flex items-center justify-between mb-4">
                          <div className="p-3 bg-orange-100 rounded-xl">
                            <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-bold text-orange-700 ">
                              {observerProgress.data.data.observer_progress.timeline_progress.afternoon.percentage}%
                            </div>
                            <div className="text-xs text-orange-600 xl:text-sm">Complete</div>
                          </div>
                        </div>
                        <div className="mb-1 text-lg font-semibold text-orange-800 xl:text-xl">Afternoon Reports</div>
                        <div className="mb-3 text-sm text-orange-700 xl:text-base">
                          {observerProgress.data.data.observer_progress.timeline_progress.afternoon.reported} of {observerProgress.data.data.observer_progress.timeline_progress.afternoon.assigned} centers
                        </div>
                        {/* Progress bar */}
                        <div className="w-full h-2 mb-1 bg-orange-200 rounded-full">
                          <div
                            className="h-2 transition-all duration-300 bg-orange-500 rounded-full"
                            style={{ width: `${observerProgress.data.data.observer_progress.timeline_progress.afternoon.percentage}%` }}
                          ></div>
                        </div>
                        <div className="text-xs text-orange-600 xl:text-sm">
                          {observerProgress.data.data.observer_progress.timeline_progress.afternoon.centers.length > 0 &&
                            `Last: ${observerProgress.data.data.observer_progress.timeline_progress.afternoon.centers.slice(-1)[0]}`
                          }
                        </div>
                        {/* Answered vs Not Answered list for Afternoon */}
                        {!(observerLookupsLoading || observerProgressLoading) && assignedCenters.length > 0 && (
                          <div className="mt-4 space-y-3">
                            <div>
                              <div className="mb-1 text-xs font-semibold text-orange-800">Answered ({answeredByTimeline.afternoon.size})</div>
                              <div className="flex flex-wrap gap-1">
                                {Array.from(answeredByTimeline.afternoon).map((c) => (
                                  <span key={`pm-ans-${c}`} className="px-2 py-0.5 text-xs text-orange-900 bg-orange-100 rounded-full border border-orange-200">{c}</span>
                                ))}
                                {answeredByTimeline.afternoon.size === 0 && (
                                  <span className="text-xs text-orange-700">No centers answered yet</span>
                                )}
                              </div>
                            </div>
                            <div>
                              <div className="mb-1 text-xs font-semibold text-red-800">Not Answered ({missingByTimeline.afternoon.length})</div>
                              <div className="flex flex-wrap gap-1">
                                {missingByTimeline.afternoon.map((c) => (
                                  <span key={`pm-miss-${c}`} className="px-2 py-0.5 text-xs text-red-800 bg-red-100 rounded-full border border-red-200">{c}</span>
                                ))}
                                {missingByTimeline.afternoon.length === 0 && (
                                  <span className="text-xs text-orange-700">All assigned centers answered</span>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Vote Count Progress */}
                    <div className="relative p-6 overflow-hidden transition-all duration-300 border border-purple-200 group bg-gradient-to-br from-purple-50 via-indigo-50 to-purple-100 rounded-lg hover:shadow-lg hover:scale-105">
                      <div className="absolute top-0 right-0 w-20 h-20 transform translate-x-6 -translate-y-6 bg-purple-500 rounded-full opacity-10"></div>
                      <div className="relative z-10">
                        <div className="flex items-center justify-between mb-4">
                          <div className="p-3 bg-purple-100 rounded-xl">
                            <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-bold text-purple-700 ">
                              {observerProgress.data.data.observer_progress.timeline_progress.vote_count.percentage}%
                            </div>
                            <div className="text-xs text-purple-600 xl:text-sm">Complete</div>
                          </div>
                        </div>
                        <div className="mb-1 text-lg font-semibold text-purple-800 xl:text-xl">Vote Count Reports</div>
                        <div className="mb-3 text-sm text-purple-700 xl:text-base">
                          {observerProgress.data.data.observer_progress.timeline_progress.vote_count.reported} of {observerProgress.data.data.observer_progress.timeline_progress.vote_count.assigned} centers
                        </div>
                        {/* Progress bar */}
                        <div className="w-full h-2 mb-1 bg-purple-200 rounded-full">
                          <div
                            className="h-2 transition-all duration-300 bg-purple-500 rounded-full"
                            style={{ width: `${observerProgress.data.data.observer_progress.timeline_progress.vote_count.percentage}%` }}
                          ></div>
                        </div>
                        <div className="text-xs text-purple-600 xl:text-sm">
                          {observerProgress.data.data.observer_progress.timeline_progress.vote_count.centers.length > 0 &&
                            `Last: ${observerProgress.data.data.observer_progress.timeline_progress.vote_count.centers.slice(-1)[0]}`
                          }
                        </div>
                        {/* Answered vs Not Answered list for Vote Count */}
                        {!(observerLookupsLoading || observerProgressLoading) && assignedCenters.length > 0 && (
                          <div className="mt-4 space-y-3">
                            <div>
                              <div className="mb-1 text-xs font-semibold text-purple-800">Answered ({answeredByTimeline.vote_count.size})</div>
                              <div className="flex flex-wrap gap-1">
                                {Array.from(answeredByTimeline.vote_count).map((c) => (
                                  <span key={`vc-ans-${c}`} className="px-2 py-0.5 text-xs text-purple-900 bg-purple-100 rounded-full border border-purple-200">{c}</span>
                                ))}
                                {answeredByTimeline.vote_count.size === 0 && (
                                  <span className="text-xs text-purple-700">No centers answered yet</span>
                                )}
                              </div>
                            </div>
                            <div>
                              <div className="mb-1 text-xs font-semibold text-red-800">Not Answered ({missingByTimeline.vote_count.length})</div>
                              <div className="flex flex-wrap gap-1">
                                {missingByTimeline.vote_count.map((c) => (
                                  <span key={`vc-miss-${c}`} className="px-2 py-0.5 text-xs text-red-800 bg-red-100 rounded-full border border-red-200">{c}</span>
                                ))}
                                {missingByTimeline.vote_count.length === 0 && (
                                  <span className="text-xs text-purple-700">All assigned centers answered</span>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Overall Progress Summary */}
                  <div className="p-6 mt-8 text-center border border-gray-200 bg-white/70 backdrop-blur-sm rounded-xl">
                    <div className="mb-1 text-2xl font-bold text-blue-600 xl:text-4xl">
                      {observerProgress.data.data.observer_progress.summary.overall_progress_percentage}%
                    </div>
                    <div className="mb-1 text-sm text-gray-600 xl:text-base">Overall Progress</div>
                    <div className="text-xs text-gray-500 xl:text-sm">
                      {observerProgress.data.data.observer_progress.summary.total_unique_centers_reported} unique centers reported across all timelines
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid max-w-4xl grid-cols-1 gap-4 mx-auto mt-12 sm:grid-cols-3">
                  <div className="p-4 border border-gray-200 bg-white/70 backdrop-blur-sm rounded-xl">
                    <div className="text-sm font-bold text-blue-600">Active</div>
                    <div className="text-sm text-gray-600">Observer Status</div>
                  </div>
                  <div className="p-4 border border-gray-200 bg-white/70 backdrop-blur-sm rounded-xl">
                    <div className="text-sm font-bold text-green-600">24/7</div>
                    <div className="text-sm text-gray-600">Support Available</div>
                  </div>
                  <div className="p-4 border border-gray-200 bg-white/70 backdrop-blur-sm rounded-xl">
                    <div className="text-sm font-bold text-purple-600">Secure</div>
                    <div className="text-sm text-gray-600">Data Protection</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Unknown roles: show simple error page
  if (user && !['admin', 'coordinator', 'observer'].includes(user.role)) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="p-6 text-center bg-white border shadow-sm rounded-xl">
          <h2 className="mb-1 text-xl font-semibold text-gray-900">Access Restricted</h2>
          <p className="mb-4 text-gray-600">Your account role does not have a dashboard view configured.</p>
          <button onClick={() => navigate('/')} className="px-4 py-2 text-white bg-blue-600 rounded hover:bg-blue-700">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  // Progressive loading: Only show full screen loader if overview (most critical) is loading
  // Other data will load progressively with individual loading states
  if (overviewLoading) {
    return <LoadingSpinner fullScreen />;
  }

  // If overview (most critical) failed and we have no data, show an error state
  if (!overview && !overviewLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100">
        <div className="p-8 text-center bg-white border shadow-xl rounded-2xl">
          <div className="p-4 mb-6 bg-red-50 rounded-lg">
            <svg className="w-12 h-12 mx-auto text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.732 14c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <h2 className="mb-1 text-xl font-semibold text-gray-900">Dashboard Unavailable</h2>
            <p className="mb-4 text-gray-600">Unable to load dashboard data. This might be due to network issues or server maintenance.</p>
            <div className="space-y-2">
              <button
                onClick={() => window.location.reload()}
                className="w-full px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Retry Dashboard
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const stats = overview?.data.data;
  const pollingData = pollingStations?.data.data;
  const complianceData = compliance?.data.data;
  const performanceData = performance?.data.data;

  const statsCards = [
    {
      title: 'Total Reports',
      value: stats?.reports?.total_reports || 0,
      icon: DocumentTextIcon,
      color: 'blue' as const,
      subtext: `${stats?.reports?.submitted || 0} submitted`,
    },
    {
      title: 'Total Reported Incidences',
      value: stats?.incidents?.total_incidents || 0,
      icon: ExclamationTriangleIcon,
      color: 'red' as const,
      subtext: `${stats?.incidents?.critical_incidents || 0} critical`,
    },
    {
      title: 'Active Users',
      value: stats?.users?.active_users || 0,
      icon: UsersIcon,
      color: 'green' as const,
      subtext: `${stats?.users?.observers || 0} observers`,
    },
    {
      title: 'SMS Messages',
      value: stats?.sms?.total_sms || 0,
      icon: ChatBubbleLeftRightIcon,
      color: 'purple' as const,
      subtext: `${stats?.sms?.processed_sms || 0} processed`,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100">
      <div className="container px-4 py-4 mx-auto space-y-4 sm:px-6 lg:px-8">
        {/* Compact Header */}
        <div className="p-2 border border-gray-200 shadow-md bg-gradient-to-r from-white via-blue-50 to-indigo-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-sm font-bold text-transparent bg-gradient-to-r from-gray-900 via-blue-900 to-indigo-900 bg-clip-text">
                Election Command Center
              </h1>
              <p className="text-xs text-gray-600">Real-time monitoring dashboard</p>
            </div>
            <div className="flex items-center px-2 py-1 bg-green-100 rounded-full">
              <div className="w-1 h-1 mr-1.5 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-xs font-medium text-green-700">Live</span>
            </div>
          </div>
        </div>


        {/* Overview - Comprehensive */}
        <div className="overflow-hidden bg-white border border-gray-200 shadow-xl rounded-2xl">
          <div className="relative px-6 py-4 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500">
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white xl:text-2xl">Overview</h3>
                <div className="flex items-center space-x-3">
                  <div className="px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm">
                    <span className="text-xs font-medium text-white">Total:</span>
                  </div>
                  <div className="text-xl font-bold text-white">
                    {overviewLoading ? (
                      <div className="w-12 h-6 bg-white/20 rounded animate-pulse"></div>
                    ) : (
                      (() => {
                        const total = Number(stats?.incidents?.reported_incidents || 0) +
                                    Number(stats?.incidents?.investigating_incidents || 0) +
                                    Number(stats?.incidents?.resolved_incidents || 0) +
                                    Number(stats?.incidents?.dismissed_incidents || 0) +
                                    Number(stats?.incidents?.uncompliant_incidents || 0) +
                                    Number(stats?.incidents?.sent_for_investigation_incidents || 0) +
                                    Number(stats?.incidents?.sent_for_intervention_incidents || 0) +
                                    Number(stats?.incidents?.documented_incidents || 0) +
                                    Number(stats?.incidents?.intervened_incidents || 0);
                        return total.toLocaleString();
                      })()
                    )}
                  </div>
                  <div className="relative">
                    <div className="w-2 h-2 bg-white rounded-full animate-ping"></div>
                    <div className="absolute inset-0 w-2 h-2 rounded-full bg-white/80"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="p-6">
            {overviewLoading ? (
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {[...Array(10)].map((_, index) => (
                  <div key={index} className="p-4 border border-gray-200 rounded-xl">
                    <div className="w-full h-4 mb-1 bg-gray-200 rounded animate-pulse"></div>
                    <div className="w-16 h-8 mb-1 bg-gray-200 rounded animate-pulse"></div>
                    <div className="w-20 h-3 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                {/* Station Operations Summary Rectangle */}
                <div className="p-4 mb-6 border border-gray-300 rounded-lg shadow-md bg-gradient-to-r from-gray-50 via-blue-50 to-gray-50">
                  <h4 className="mb-3 text-sm font-semibold text-gray-700">Station Operations Summary</h4>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                {/* Station Operations Summary - Compact */}
                <div className="relative p-1.5 overflow-hidden transition-all duration-300 border border-green-200 group bg-gradient-to-br from-green-50 via-emerald-50 to-green-100 rounded-lg hover:shadow-lg">
                  <div className="absolute top-0 right-0 w-6 h-6 transform translate-x-1 -translate-y-1 bg-green-500 rounded-full opacity-10"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-1">
                        <div className="p-0.5 bg-green-100 rounded">
                          <svg className="w-2.5 h-2.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h4m6 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-green-600">Stations Reporting</div>
                          <div className="text-xs text-green-600">
                            {pollingStationsLoading ? (
                              <div className="w-16 h-2 bg-green-200 rounded animate-pulse"></div>
                            ) : (
                              `${pollingData?.station_operations?.total_reported_stations?.toLocaleString() || 0} of ${pollingData?.station_operations?.total_stations?.toLocaleString() || 0}`
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold text-green-700">
                          {pollingStationsLoading ? (
                            <div className="w-8 h-4 bg-green-200 rounded animate-pulse"></div>
                          ) : (
                            (() => {
                              const reported = pollingData?.station_operations?.total_reported_stations || 0;
                              const total = pollingData?.station_operations?.total_stations || 1;
                              return ((reported / total) * 100).toFixed(1);
                            })() + '%'
                          )}
                        </div>
                        <div className="w-1 h-1 bg-green-500 rounded-full animate-pulse ml-auto"></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* MEC Staff Present - Compact */}
                <div className="relative p-1.5 overflow-hidden transition-all duration-300 border border-blue-200 group bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 rounded-lg hover:shadow-lg">
                  <div className="absolute top-0 right-0 w-6 h-6 transform translate-x-1 -translate-y-1 bg-blue-500 rounded-full opacity-10"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-1">
                        <div className="p-0.5 bg-blue-100 rounded">
                          <UsersIcon className="w-2.5 h-2.5 text-blue-600" />
                        </div>
                        <div>
                          <div className="text-xs font-medium text-blue-600">MEC Staff Present</div>
                          <div className="text-xs text-blue-600">
                            {pollingStationsLoading ? (
                              <div className="w-16 h-2 bg-blue-200 rounded animate-pulse"></div>
                            ) : (
                              `${pollingData?.staff_presence?.mec_staff?.stations_represented?.toLocaleString() || 0} of ${pollingData?.staff_presence?.total_expected_stations?.toLocaleString() || 0}`
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold text-blue-700">
                          {pollingStationsLoading ? (
                            <div className="w-8 h-4 bg-blue-200 rounded animate-pulse"></div>
                          ) : (
                            `${pollingData?.staff_presence?.mec_staff?.representation_percentage?.toFixed(1) || 0}%`
                          )}
                        </div>
                        <div className="w-1 h-1 bg-blue-500 rounded-full animate-pulse ml-auto"></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Reporting Compliance - Compact */}
                <div className="relative p-1.5 overflow-hidden transition-all duration-300 border border-purple-200 group bg-gradient-to-br from-purple-50 via-violet-50 to-purple-100 rounded-lg hover:shadow-lg">
                  <div className="absolute top-0 right-0 w-6 h-6 transform translate-x-1 -translate-y-1 bg-purple-500 rounded-full opacity-10"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-1">
                        <div className="p-0.5 bg-purple-100 rounded">
                          <DocumentTextIcon className="w-2.5 h-2.5 text-purple-600" />
                        </div>
                        <div>
                          <div className="text-xs font-medium text-purple-600">Report Compliance</div>
                          <div className="text-xs text-purple-600">Average timelines</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold text-purple-700">
                          {complianceLoading ? (
                            <div className="w-8 h-4 bg-purple-200 rounded animate-pulse"></div>
                          ) : (
                            (() => {
                              const timelines = complianceData?.timeline_compliance || {};
                              const avg = (timelines.morning + timelines.afternoon + timelines.vote_count) / 3;
                              return avg.toFixed(1);
                            })() + '%'
                          )}
                        </div>
                        <div className="w-1 h-1 bg-purple-500 rounded-full animate-pulse ml-auto"></div>
                      </div>
                    </div>
                  </div>
                </div>
                  </div>
                </div>

                {/* Incident Status Breakdown Rectangle */}
                <div className="p-4 border border-gray-300 rounded-lg shadow-md bg-gradient-to-r from-gray-50 via-red-50 to-gray-50">
                  <h4 className="mb-3 text-sm font-semibold text-gray-700">Incident Status Breakdown</h4>
                  <div className="grid grid-cols-3 gap-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">

                {/* Total Reported Incidences - Enhanced */}
                <div className="relative p-1.5 overflow-hidden transition-all duration-300 border border-red-200 group bg-gradient-to-br from-red-50 via-pink-50 to-red-100 rounded-lg hover:shadow-md hover:scale-102">
                  <div className="absolute top-0 right-0 w-6 h-6 transform translate-x-1 -translate-y-1 bg-red-500 rounded-full opacity-10"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-1">
                      <span className="inline-block w-2 h-2 bg-red-500 rounded-full"></span>
                      <div className="relative">
                        <div className="w-1 h-1 bg-red-500 rounded-full animate-ping"></div>
                        <div className="absolute inset-0 w-1 h-1 bg-red-400 rounded-full"></div>
                      </div>
                    </div>
                    <div className="text-sm font-bold text-red-700">
                      {(() => {
                        const total = Number(stats?.incidents?.reported_incidents || 0) +
                                    Number(stats?.incidents?.investigating_incidents || 0) +
                                    Number(stats?.incidents?.resolved_incidents || 0) +
                                    Number(stats?.incidents?.dismissed_incidents || 0) +
                                    Number(stats?.incidents?.uncompliant_incidents || 0) +
                                    Number(stats?.incidents?.sent_for_investigation_incidents || 0) +
                                    Number(stats?.incidents?.sent_for_intervention_incidents || 0) +
                                    Number(stats?.incidents?.documented_incidents || 0) +
                                    Number(stats?.incidents?.intervened_incidents || 0);
                        return total.toLocaleString();
                      })()}
                    </div>
                    <div className="text-xs font-medium text-red-600">Total Incidents</div>
                  </div>
                </div>
                {/* Reported */}
                <div className="relative p-1.5 overflow-hidden transition-all duration-300 border border-amber-200 group bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100 rounded-lg hover:shadow-md hover:scale-102">
                  <div className="absolute top-0 right-0 w-6 h-6 transform translate-x-1 -translate-y-1 bg-amber-500 rounded-full opacity-10"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-1">
                      <span className="inline-block w-2 h-2 bg-amber-500 rounded-full"></span>
                      <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                    </div>
                    <div className="text-sm font-bold text-amber-700 ">
                      {stats?.incidents?.reported_incidents || 0}
                    </div>
                    <div className="text-xs font-medium text-amber-600">Reported</div>
                    <div className="text-xs text-amber-600">New incidents</div>
                  </div>
                </div>

                {/* Investigating */}
                <div className="relative p-1.5 overflow-hidden transition-all duration-300 border border-blue-200 group bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 rounded-lg hover:shadow-md hover:scale-102">
                  <div className="absolute top-0 right-0 w-6 h-6 transform translate-x-1 -translate-y-1 bg-blue-500 rounded-full opacity-10"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-1">
                      <span className="inline-block w-2 h-2 bg-blue-500 rounded-full"></span>
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                    </div>
                    <div className="text-sm font-bold text-blue-700 ">
                      {stats?.incidents?.investigating_incidents || 0}
                    </div>
                    <div className="text-xs font-medium text-blue-600">Investigating</div>
                    <div className="text-xs text-blue-600">Under review</div>
                  </div>
                </div>

                {/* Resolved */}
                <div className="relative p-1.5 overflow-hidden transition-all duration-300 border border-green-200 group bg-gradient-to-br from-green-50 via-emerald-50 to-green-100 rounded-lg hover:shadow-md hover:scale-102">
                  <div className="absolute top-0 right-0 w-6 h-6 transform translate-x-1 -translate-y-1 bg-green-500 rounded-full opacity-10"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-1">
                      <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    </div>
                    <div className="text-sm font-bold text-green-700 ">
                      {stats?.incidents?.resolved_incidents || 0}
                    </div>
                    <div className="text-xs font-medium text-green-600">Resolved</div>
                    <div className="text-xs text-green-600">Successfully closed</div>
                  </div>
                </div>

                {/* Dismissed */}
                <div className="relative p-1.5 overflow-hidden transition-all duration-300 border border-red-200 group bg-gradient-to-br from-red-50 via-pink-50 to-red-100 rounded-lg hover:shadow-md hover:scale-102">
                  <div className="absolute top-0 right-0 w-6 h-6 transform translate-x-1 -translate-y-1 bg-red-500 rounded-full opacity-10"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-1">
                      <span className="inline-block w-2 h-2 bg-red-500 rounded-full"></span>
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                    </div>
                    <div className="text-sm font-bold text-red-700 ">
                      {stats?.incidents?.dismissed_incidents || 0}
                    </div>
                    <div className="text-xs font-medium text-red-600">Dismissed</div>
                    <div className="text-xs text-red-600">Not valid</div>
                  </div>
                </div>

                {/* Uncompliant */}
                <div className="relative p-1.5 overflow-hidden transition-all duration-300 border border-orange-200 group bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 rounded-lg hover:shadow-md hover:scale-102">
                  <div className="absolute top-0 right-0 w-6 h-6 transform translate-x-1 -translate-y-1 bg-orange-500 rounded-full opacity-10"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-1">
                      <span className="inline-block w-2 h-2 bg-orange-500 rounded-full"></span>
                      <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                    </div>
                    <div className="text-sm font-bold text-orange-700 ">
                      {stats?.incidents?.uncompliant_incidents || 0}
                    </div>
                    <div className="text-xs font-medium text-orange-600">Uncompliant</div>
                    <div className="text-xs text-orange-600">Rejected</div>
                  </div>
                </div>

                {/* Sent for Investigation */}
                <div className="relative p-1.5 overflow-hidden transition-all duration-300 border border-cyan-200 group bg-gradient-to-br from-cyan-50 via-blue-50 to-cyan-100 rounded-lg hover:shadow-md hover:scale-102">
                  <div className="absolute top-0 right-0 w-6 h-6 transform translate-x-1 -translate-y-1 bg-cyan-500 rounded-full opacity-10"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-1">
                      <span className="inline-block w-2 h-2 bg-cyan-500 rounded-full"></span>
                      <div className="w-2 h-2 bg-cyan-500 rounded-full animate-pulse"></div>
                    </div>
                    <div className="text-sm font-bold text-cyan-700 ">
                      {stats?.incidents?.sent_for_investigation_incidents || 0}
                    </div>
                    <div className="text-xs font-medium text-cyan-600">Sent for Investigation</div>
                    <div className="text-xs text-cyan-600">Escalated</div>
                  </div>
                </div>

                {/* Sent for Intervention */}
                <div className="relative p-1.5 overflow-hidden transition-all duration-300 border border-purple-200 group bg-gradient-to-br from-purple-50 via-violet-50 to-purple-100 rounded-lg hover:shadow-md hover:scale-102">
                  <div className="absolute top-0 right-0 w-6 h-6 transform translate-x-1 -translate-y-1 bg-purple-500 rounded-full opacity-10"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-1">
                      <span className="inline-block w-2 h-2 bg-purple-500 rounded-full"></span>
                      <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                    </div>
                    <div className="text-sm font-bold text-purple-700 ">
                      {stats?.incidents?.sent_for_intervention_incidents || 0}
                    </div>
                    <div className="text-xs font-medium text-purple-600">Sent for Intervention</div>
                    <div className="text-xs text-purple-600">Requires action</div>
                  </div>
                </div>

                {/* Documented */}
                <div className="relative p-1.5 overflow-hidden transition-all duration-300 border border-teal-200 group bg-gradient-to-br from-teal-50 via-cyan-50 to-teal-100 rounded-lg hover:shadow-md hover:scale-102">
                  <div className="absolute top-0 right-0 w-6 h-6 transform translate-x-1 -translate-y-1 bg-teal-500 rounded-full opacity-10"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-1">
                      <span className="inline-block w-2 h-2 bg-teal-500 rounded-full"></span>
                      <div className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></div>
                    </div>
                    <div className="text-sm font-bold text-teal-700 ">
                      {stats?.incidents?.documented_incidents || 0}
                    </div>
                    <div className="text-xs font-medium text-teal-600">Documented</div>
                    <div className="text-xs text-teal-600">Recorded</div>
                  </div>
                </div>

                {/* Intervened */}
                <div className="relative p-1.5 overflow-hidden transition-all duration-300 border border-emerald-200 group bg-gradient-to-br from-emerald-50 via-green-50 to-emerald-100 rounded-lg hover:shadow-md hover:scale-102">
                  <div className="absolute top-0 right-0 w-6 h-6 transform translate-x-1 -translate-y-1 bg-emerald-500 rounded-full opacity-10"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-1">
                      <span className="inline-block w-2 h-2 bg-emerald-500 rounded-full"></span>
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                    </div>
                    <div className="text-sm font-bold text-emerald-700 ">
                      {stats?.incidents?.intervened_incidents || 0}
                    </div>
                    <div className="text-xs font-medium text-emerald-600">Intervened</div>
                    <div className="text-xs text-emerald-600">Action taken</div>
                  </div>
                </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Station Operations + Map Section - Enhanced Layout */}
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          {/* Incidents Map Section - Enhanced */}
          <div className="xl:col-span-2">
            <div className="overflow-hidden bg-white border border-gray-200 shadow-xl rounded-2xl">
              <div className="relative px-6 py-4 bg-gradient-to-r from-red-500 via-pink-500 to-rose-500">
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold text-white xl:text-2xl">Incidents Map</h3>
                    <div className="flex items-center space-x-3">
                      <div className="px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm">
                        <span className="text-xs font-medium text-white">Total:</span>
                      </div>
                      <div className="text-sm font-bold text-white">
                        <AnimatedCounter
                          totalIncidents={incidentsMap?.data?.map_incidents?.total_incidents || stats?.incidents?.total_incidents || 0}
                          textColor="text-white"
                        />
                      </div>
                      <div className="relative">
                        <div className="w-2 h-2 bg-white rounded-full animate-ping"></div>
                        <div className="absolute inset-0 w-2 h-2 rounded-full bg-white/80"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="relative overflow-hidden bg-gray-50 rounded-xl">
                  {incidentsMapError ? (
                    <div className="flex flex-col items-center justify-center h-[520px] p-8">
                      <div className="p-4 mb-4 bg-red-50 rounded-lg">
                        <svg className="w-8 h-8 mx-auto text-red-500 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.732 14c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <p className="text-xs font-medium text-red-700 text-center">Failed to load incidents map</p>
                        <p className="text-xs text-red-600 text-center mt-1">
                          {incidentsMapError?.code === 'ECONNABORTED' ? 'Request timed out' : 'Connection error'}
                        </p>
                      </div>
                      <button
                        onClick={() => window.location.reload()}
                        className="px-4 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        Retry
                      </button>
                    </div>
                  ) : (
                    <>
                      <MalawiDistrictsMap
                        height={520}
                        incidentData={incidentsMap?.data}
                      />
                      {incidentsMapLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm">
                          <div className="text-center">
                            <div className="w-8 h-8 mx-auto mb-1 border-4 border-red-200 rounded-full border-t-red-500 animate-spin"></div>
                            <p className="text-xs font-medium text-gray-600">Loading map data...</p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Incident Distribution Summary */}
                {incidentsMap?.data?.map_incidents && (
                  <div className="mt-4 p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                    <h4 className="text-sm font-semibold text-white mb-3 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 00-2-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4" />
                      </svg>
                      District Distribution
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                      {(() => {
                        const districts = incidentsMap.data.map_incidents.districts || [];

                        // Calculate dynamic ranges for the distribution
                        const allCounts: number[] = districts.map((d: any) => d.incident_count || 0);
                        const nonZeroCounts = allCounts.filter((count: number) => count > 0).sort((a: number, b: number) => a - b);

                        if (nonZeroCounts.length === 0) {
                          return [
                            { label: 'No Incidents', count: districts.length, color: 'bg-gray-100' }
                          ].map((item, index) => (
                            <div key={index} className="flex items-center justify-between p-2 bg-white/20 rounded-lg">
                              <div className="flex items-center space-x-1">
                                <span className={`inline-block w-2 h-2 rounded-full ${item.color}`}></span>
                                <span className="text-white font-medium">{item.label}</span>
                              </div>
                              <span className="text-white font-bold">{item.count}</span>
                            </div>
                          ));
                        }

                        const min = nonZeroCounts[0];
                        const max = nonZeroCounts[nonZeroCounts.length - 1];
                        const range = max - min;
                        const quintileSize = range / 5;

                        const ranges = {
                          veryLow: { min: min, max: Math.ceil(min + quintileSize) },
                          low: { min: Math.ceil(min + quintileSize) + 1, max: Math.ceil(min + quintileSize * 2) },
                          medium: { min: Math.ceil(min + quintileSize * 2) + 1, max: Math.ceil(min + quintileSize * 3) },
                          high: { min: Math.ceil(min + quintileSize * 3) + 1, max: Math.ceil(min + quintileSize * 4) },
                          critical: { min: Math.ceil(min + quintileSize * 4) + 1, max: max }
                        };

                        const distribution = {
                          none: districts.filter((d: any) => d.incident_count === 0).length,
                          veryLow: districts.filter((d: any) => d.incident_count >= ranges.veryLow.min && d.incident_count <= ranges.veryLow.max).length,
                          low: districts.filter((d: any) => d.incident_count >= ranges.low.min && d.incident_count <= ranges.low.max).length,
                          medium: districts.filter((d: any) => d.incident_count >= ranges.medium.min && d.incident_count <= ranges.medium.max).length,
                          high: districts.filter((d: any) => d.incident_count >= ranges.high.min && d.incident_count <= ranges.high.max).length,
                          critical: districts.filter((d: any) => d.incident_count >= ranges.critical.min).length,
                        };

                        return [
                          { label: 'No Incidents', count: distribution.none, color: 'bg-gray-100' },
                          { label: `Very Low (${ranges.veryLow.min}-${ranges.veryLow.max})`, count: distribution.veryLow, color: 'bg-blue-100' },
                          { label: `Low (${ranges.low.min}-${ranges.low.max})`, count: distribution.low, color: 'bg-blue-200' },
                          { label: `Medium (${ranges.medium.min}-${ranges.medium.max})`, count: distribution.medium, color: 'bg-amber-400' },
                          { label: `High (${ranges.high.min}-${ranges.high.max})`, count: distribution.high, color: 'bg-amber-500' },
                          { label: `Critical (${ranges.critical.min}+)`, count: distribution.critical, color: 'bg-red-600' },
                        ].map((item, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-white/20 rounded-lg">
                            <div className="flex items-center space-x-1">
                              <span className={`inline-block w-2 h-2 rounded-full ${item.color}`}></span>
                              <span className="text-white font-medium text-xs">{item.label}</span>
                            </div>
                            <span className="text-white font-bold">{item.count}</span>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                )}

                {/* Incident Status Statistics */}
                {incidentsMap?.data?.map_incidents && (
                  <div className="mt-4 p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                    <h4 className="text-sm font-semibold text-white mb-3 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Status Overview (Excluding Investigating)
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      {(() => {
                        const statusCounts = incidentsMap.data.map_incidents.status_counts || {};

                        return [
                          { label: 'Reported', count: statusCounts.reported || 0, color: 'bg-amber-400' },
                          { label: 'Resolved', count: statusCounts.resolved || 0, color: 'bg-green-500' },
                          { label: 'Dismissed', count: statusCounts.dismissed || 0, color: 'bg-red-500' },
                          { label: 'Uncompliant', count: statusCounts.uncompliant || 0, color: 'bg-orange-500' },
                          { label: 'Sent for Investigation', count: statusCounts.sent_for_investigation || 0, color: 'bg-blue-500' },
                          { label: 'Sent for Intervention', count: statusCounts.sent_for_intervention || 0, color: 'bg-purple-500' },
                          { label: 'Documented', count: statusCounts.documented || 0, color: 'bg-teal-500' },
                          { label: 'Intervened', count: statusCounts.intervened || 0, color: 'bg-emerald-500' },
                        ].filter(item => item.count > 0).map((item, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-white/20 rounded-lg">
                            <div className="flex items-center space-x-1">
                              <span className={`inline-block w-2 h-2 rounded-full ${item.color}`}></span>
                              <span className="text-white font-medium text-xs">{item.label}</span>
                            </div>
                            <span className="text-white font-bold">{item.count}</span>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Polling Station Operations - Enhanced */}
          <div className="xl:col-span-1">
            <div className="overflow-hidden bg-white border border-gray-200 shadow-xl rounded-2xl">
              <div className="relative px-6 py-4 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500">
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="relative z-10">
                  <h2 className="flex items-center text-xl font-bold text-white xl:text-2xl">
                    <div className="p-2 mr-3 bg-white/20 backdrop-blur-sm rounded-xl">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h4m6 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    Polling Station Operations
                    <div className="flex items-center ml-4 space-x-1">
                      <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                      <span className="text-xs font-medium text-white/90">LIVE</span>
                    </div>
                  </h2>
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 gap-8 mb-8 lg:grid-cols-2">
                  {/* Station Status - Enhanced */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-bold text-gray-900">Opening Status</h3>
                      <div className="px-3 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                        Real-time
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="relative p-1.5 overflow-hidden transition-all duration-300 border border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg hover:shadow-lg">
                        <div className="absolute top-0 right-0 w-12 h-12 transform translate-x-3 -translate-y-3 bg-green-500 rounded-full opacity-10"></div>
                        <div className="relative z-10">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className="w-2 h-2 mr-3 bg-green-500 rounded-full animate-pulse"></div>
                              <span className="font-medium text-gray-900">Opened on Time</span>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-bold text-green-700 ">
                                {pollingStationsLoading ? (
                                  <div className="w-16 h-8 bg-green-200 rounded animate-pulse"></div>
                                ) : (
                                  (() => {
                                    const percentage = pollingData?.station_operations?.morning?.opened_on_time_percentage;
                                    if (percentage && percentage > 0) {
                                      return percentage.toFixed(1);
                                    }
                                    const onTime = parseInt(pollingData?.station_operations?.morning?.opened_on_time || '0');
                                    const total = pollingData?.station_operations?.morning?.reported_stations || 0;
                                    if (onTime > 0 && total > 0) {
                                      return ((onTime / total) * 100).toFixed(1);
                                    }
                                    return '0.0';
                                  })() + '%'
                                )}
                              </div>
                              <div className="text-sm text-green-600">
                                {pollingStationsLoading ? (
                                  <div className="w-20 h-4 bg-green-200 rounded animate-pulse"></div>
                                ) : (
                                  `${pollingData?.station_operations?.morning?.opened_on_time?.toLocaleString() || 0} stations`
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="relative p-1.5 overflow-hidden transition-all duration-300 border border-red-200 bg-gradient-to-r from-red-50 to-pink-50 rounded-lg hover:shadow-lg">
                        <div className="absolute top-0 right-0 w-12 h-12 transform translate-x-3 -translate-y-3 bg-red-500 rounded-full opacity-10"></div>
                        <div className="relative z-10">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className="w-2 h-2 mr-3 bg-red-500 rounded-full animate-pulse"></div>
                              <span className="font-medium text-gray-900">Not Yet Opened</span>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-bold text-red-700 ">
                                {(() => {
                                  const total = pollingData?.station_operations?.total_stations || 0;
                                  const reported = pollingData?.station_operations?.total_reported_stations || 0;
                                  const notOpened = total - reported;
                                  const percentage = total > 0 ? ((notOpened / total) * 100).toFixed(1) : '0.0';
                                  return percentage;
                                })()}%
                              </div>
                              <div className="text-sm text-red-600">
                                {(() => {
                                  const total = pollingData?.station_operations?.total_stations || 0;
                                  const reported = pollingData?.station_operations?.total_reported_stations || 0;
                                  const notOpened = total - reported;
                                  return notOpened.toLocaleString();
                                })()} of {pollingData?.station_operations?.total_stations?.toLocaleString() || 0}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Late Opening Reasons - Enhanced */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-bold text-gray-900">Delay Analysis</h3>
                      <div className="px-3 py-1 text-xs font-medium text-orange-700 bg-orange-100 rounded-full">
                        {pollingData?.delay_reasons?.stations_with_delays?.toLocaleString() || 0} affected
                      </div>
                    </div>

                    {/* Enhanced Stations with Delays Summary */}
                    <div className="relative p-1.5 overflow-hidden transition-all duration-300 border border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg hover:shadow-lg">
                      <div className="absolute top-0 right-0 w-12 h-12 transform translate-x-3 -translate-y-3 bg-orange-500 rounded-full opacity-10"></div>
                      <div className="relative z-10">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-2 h-2 mr-3 bg-orange-500 rounded-full animate-pulse"></div>
                            <span className="font-medium text-gray-900">Stations with Delays</span>
                          </div>
                          <div className="text-sm font-bold text-orange-700">
                            {pollingData?.delay_reasons?.stations_with_delays?.toLocaleString() || 0}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Enhanced Scrollable Reasons List */}
                    <div className="p-4 bg-gray-50/50 rounded-xl">
                      <h4 className="mb-3 text-sm font-bold tracking-wide text-gray-700 uppercase">Breakdown by Reason</h4>
                      <div className="pr-2 space-y-3 overflow-y-auto max-h-40" style={{ scrollbarWidth: 'thin' }}>
                        {pollingData?.delay_reasons?.reasons?.map((reason: any, index: number) => {
                          const colors = [
                            { bg: 'from-orange-50 to-red-50', border: 'border-orange-200', text: 'text-orange-700', dot: 'bg-orange-500' },
                            { bg: 'from-red-50 to-pink-50', border: 'border-red-200', text: 'text-red-700', dot: 'bg-red-500' },
                            { bg: 'from-yellow-50 to-orange-50', border: 'border-yellow-200', text: 'text-yellow-700', dot: 'bg-yellow-500' },
                            { bg: 'from-gray-50 to-slate-50', border: 'border-gray-200', text: 'text-gray-700', dot: 'bg-gray-500' }
                          ];
                          const colorScheme = colors[index % colors.length];
                          return (
                            <div key={reason.reason} className={`relative overflow-hidden bg-gradient-to-r ${colorScheme.bg} border ${colorScheme.border} rounded-lg p-3 hover:shadow-md transition-all duration-300`}>
                              <div className="flex items-start justify-between">
                                <div className="flex items-start flex-1 mr-3">
                                  <div className={`w-2 h-2 ${colorScheme.dot} rounded-full mr-3 mt-2 flex-shrink-0`}></div>
                                  <span className="text-sm leading-relaxed text-gray-700 break-words xl:text-base">
                                    {reason.reason}
                                  </span>
                                </div>
                                <div className={`px-2 py-1 bg-white/70 backdrop-blur-sm rounded-full text-xs font-bold ${colorScheme.text} flex-shrink-0`}>
                                  {reason.count.toLocaleString()}
                                </div>
                              </div>
                            </div>
                          );
                        }) || (
                            <div className="py-8 text-center">
                              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full">
                                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </div>
                              <p className="text-xs font-medium text-gray-500">No delay data available</p>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Staff & Process Status within Operations Card */}
                <div className="grid grid-cols-1 gap-6 pt-6 border-t border-gray-200 lg:grid-cols-2">
                  {/* Staff Presence */}
                  <div>
                    <div className="relative px-4 py-3 overflow-hidden border border-blue-200 rounded-t-lg bg-gradient-to-r from-blue-50 to-blue-100">
                      <h3 className="flex items-center font-semibold text-gray-900 text-md">
                        <div className="p-1.5 bg-blue-100 rounded-lg mr-2">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        Staff Presence
                        <div className="w-2 h-2 ml-2 bg-blue-500 rounded-full animate-pulse"></div>
                      </h3>
                      <div className="absolute top-0 right-0 w-1 h-full bg-blue-400 animate-pulse"></div>
                    </div>
                    <div className="p-3 space-y-3 bg-white border-b border-l border-r border-blue-200 rounded-b-lg">
                      {/* First Row: MEC Staff and Security Personnel */}
                      <div className="grid grid-cols-2 gap-2">
                        <div className="p-2 text-center rounded-lg bg-blue-50">
                          <div className="font-bold text-blue-700 text-md">
                            {pollingData?.staff_presence?.mec_staff?.representation_percentage?.toFixed(1) || 0}%
                          </div>
                          <div className="text-xs text-blue-600">MEC Staff Present</div>
                          <div className="text-xs text-blue-500">
                            {pollingData?.staff_presence?.mec_staff?.stations_represented?.toLocaleString() || 0} of {pollingData?.staff_presence?.total_expected_stations?.toLocaleString() || 0}
                          </div>
                        </div>
                        <div className="p-2 text-center rounded-lg bg-indigo-50">
                          <div className="font-bold text-indigo-700 text-md">
                            {pollingData?.staff_presence?.security_personnel?.representation_percentage?.toFixed(1) || 0}%
                          </div>
                          <div className="text-xs text-indigo-600">Security Personnel Present</div>
                          <div className="text-xs text-indigo-500">
                            {pollingData?.staff_presence?.security_personnel?.stations_represented?.toLocaleString() || 0} of {pollingData?.staff_presence?.total_expected_stations?.toLocaleString() || 0}
                          </div>
                        </div>
                      </div>

                      {/* Second Row: Party Agents and Observers Denied Access */}
                      <div className="grid grid-cols-2 gap-2">
                        <div className="p-2 text-center rounded-lg bg-green-50">
                          <div className="font-bold text-green-700 text-md">
                            {pollingData?.staff_presence?.party_agents?.representation_percentage?.toFixed(1) || 0}%
                          </div>
                          <div className="text-xs text-green-600">Party Agents Present</div>
                          <div className="text-xs text-green-500">
                            {pollingData?.staff_presence?.party_agents?.stations_represented?.toLocaleString() || 0} of {pollingData?.staff_presence?.total_expected_stations?.toLocaleString() || 0}
                          </div>
                        </div>
                        <div className="p-2 text-center border border-red-200 rounded-lg bg-red-50">
                          <div className="font-bold text-red-700 text-md">
                            {pollingData?.staff_presence?.observers_access?.denied_access_percentage?.toFixed(1) || 0}%
                          </div>
                          <div className="text-xs text-red-600">Observers Denied Access</div>
                          <div className="text-xs text-red-500">
                            {pollingData?.staff_presence?.observers_access?.stations_denied?.toLocaleString() || 0} of {pollingData?.staff_presence?.total_expected_stations?.toLocaleString() || 0}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Process Issues */}
                  <div>
                    <div className="relative px-4 py-3 overflow-hidden border border-yellow-200 rounded-t-lg bg-gradient-to-r from-yellow-50 to-yellow-100">
                      <h3 className="flex items-center font-semibold text-gray-900 text-md">
                        <div className="p-1.5 bg-yellow-100 rounded-lg mr-2">
                          <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                        </div>
                        Process Issues
                        <div className="w-2 h-2 ml-2 bg-yellow-500 rounded-full animate-pulse"></div>
                      </h3>
                      <div className="absolute top-0 right-0 w-1 h-full bg-yellow-400 animate-pulse"></div>
                    </div>
                    <div className="p-3 space-y-2 bg-white border-b border-l border-r border-yellow-200 rounded-b-lg">
                      <div className="p-2 text-center rounded-lg bg-orange-50">
                        <div className="font-bold text-orange-700 text-md">
                          {pollingData?.process_issues?.non_moving_queues_percentage?.toFixed(1) || 0}%
                        </div>
                        <div className="text-xs text-orange-600">Non-Moving Queues</div>
                      </div>
                      <div className="p-2 text-center rounded-lg bg-yellow-50">
                        <div className="font-bold text-yellow-700 text-md">
                          {pollingData?.process_issues?.inadequate_disability_aid_percentage?.toFixed(1) || 0}%
                        </div>
                        <div className="text-xs text-yellow-600">Inadequate Disability Aid</div>
                      </div>
                      <div className="p-2 text-center border border-red-200 rounded-lg bg-red-50">
                        <div className="font-bold text-red-700 text-md">
                          {pollingData?.process_issues?.voting_suspended?.voting_suspended_percentage?.toFixed(1) || 0}%
                        </div>
                        <div className="text-xs text-red-600">Voting Suspended or Delayed</div>
                        <div className="text-xs text-red-500">
                          {pollingData?.process_issues?.voting_suspended?.stations_not_open || 0} not open / {pollingData?.process_issues?.voting_suspended?.morning_stations || 0} morning
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>


        </div>

        {/* Enhanced Reporting Compliance Details */}
        <div className="overflow-hidden bg-white border border-gray-200 shadow-xl rounded-2xl">
          <div className="relative px-6 py-4 bg-gradient-to-r from-purple-500 via-violet-500 to-indigo-500">
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="relative z-10">
              <h2 className="flex items-center text-xl font-bold text-white xl:text-2xl">
                <div className="p-2 mr-3 bg-white/20 backdrop-blur-sm rounded-xl">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                Reporting Compliance Analytics
                <div className="flex items-center ml-4 space-x-1">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                  <span className="text-xs font-medium text-white/90">LIVE</span>
                </div>
              </h2>
            </div>
          </div>

          <div className="p-6">
            {/* Enhanced Timeline Compliance Grid */}
            <div className="grid grid-cols-2 gap-2 mb-6 sm:grid-cols-2 lg:grid-cols-4">
              {/* Morning - Enhanced */}
              <div className="relative p-3 overflow-hidden transition-all duration-300 border border-green-200 group bg-gradient-to-br from-green-50 via-emerald-50 to-green-100 rounded-lg hover:shadow-lg hover:scale-105">
                <div className="absolute top-0 right-0 w-12 h-12 transform translate-x-3 -translate-y-3 bg-green-500 rounded-full opacity-10"></div>
                <div className="relative z-10 text-center">
                  <div className="mb-1 text-2xl font-bold text-green-700 xl:text-4xl">
                    {complianceData?.timeline_compliance?.morning?.toFixed(1) || 0}%
                  </div>
                  <div className="flex items-center justify-center mb-1">
                    <div className="w-2 h-2 mr-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-bold tracking-wide text-green-700 uppercase">Morning</span>
                  </div>
                  <div className="text-xs text-green-600">5:00 AM - 12:00 PM</div>
                </div>
              </div>

              {/* Afternoon - Enhanced */}
              <div className="relative p-3 overflow-hidden transition-all duration-300 border border-blue-200 group bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 rounded-lg hover:shadow-lg hover:scale-105">
                <div className="absolute top-0 right-0 w-12 h-12 transform translate-x-3 -translate-y-3 bg-blue-500 rounded-full opacity-10"></div>
                <div className="relative z-10 text-center">
                  <div className="mb-1 text-2xl font-bold text-blue-700 xl:text-4xl">
                    {complianceData?.timeline_compliance?.afternoon?.toFixed(1) || 0}%
                  </div>
                  <div className="flex items-center justify-center mb-1">
                    <div className="w-2 h-2 mr-2 bg-blue-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-bold tracking-wide text-blue-700 uppercase">Afternoon</span>
                  </div>
                  <div className="text-xs text-blue-600">12:00 PM - 4:00 PM</div>
                </div>
              </div>


              {/* Vote Count - Enhanced */}
              <div className="relative p-3 overflow-hidden transition-all duration-300 border border-orange-200 group bg-gradient-to-br from-orange-50 via-red-50 to-orange-100 rounded-lg hover:shadow-lg hover:scale-105">
                <div className="absolute top-0 right-0 w-12 h-12 transform translate-x-3 -translate-y-3 bg-orange-500 rounded-full opacity-10"></div>
                <div className="relative z-10 text-center">
                  <div className="mb-1 text-2xl font-bold text-orange-700 xl:text-4xl">
                    {complianceData?.timeline_compliance?.vote_count?.toFixed(1) || 0}%
                  </div>
                  <div className="flex items-center justify-center mb-1">
                    <div className="w-2 h-2 mr-2 bg-orange-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-bold tracking-wide text-orange-700 uppercase">Vote Count</span>
                  </div>
                  <div className="text-xs text-orange-600">8:00 PM - 4:00 AM</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="space-y-3">
                <h3 className="text-xs font-medium tracking-wide text-gray-800 uppercase">Performance</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                    <span className="text-sm text-gray-600">Avg Reports/Observer</span>
                    <span className="text-sm font-bold text-blue-600">
                      {performanceData?.avg_reports_per_observer?.toFixed(1) || '0.0'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex flex-col items-center p-1.5 bg-green-50 rounded-lg">
                      <span className="text-xs text-gray-600 mb-0.5">Best District</span>
                      <span className="text-xs font-semibold text-green-600">
                        {performanceData?.best_district ?
                          `${performanceData.best_district.name} (${performanceData.best_district.compliance_rate?.toFixed(1)}%)` :
                          'N/A'}
                      </span>
                    </div>
                    <div className="flex flex-col items-center p-1.5 bg-red-50 rounded-lg">
                      <span className="text-xs text-gray-600 mb-0.5">Needs Attention</span>
                      <span className="text-xs font-semibold text-red-600">
                        {performanceData?.worst_district ?
                          `${performanceData.worst_district.name} (${performanceData.worst_district.compliance_rate?.toFixed(1)}%)` :
                          'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="text-xs font-medium tracking-wide text-gray-800 uppercase">Overdue Alerts</h3>
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center justify-between p-2 rounded-lg bg-red-50">
                      <span className="text-xs text-gray-600">Morning</span>
                      <span className="px-1 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-semibold">
                        {complianceData?.overdue_alerts?.morning || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-2 rounded-lg bg-orange-50">
                      <span className="text-xs text-gray-600">Afternoon</span>
                      <span className="px-1 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold">
                        {complianceData?.overdue_alerts?.afternoon || 0}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center justify-between p-2 rounded-lg bg-red-50">
                      <span className="text-xs text-gray-600">Vote Count</span>
                      <span className="px-1 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-semibold">
                        {complianceData?.overdue_alerts?.vote_count || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;